import React, { JSX } from "react";
import produce from "immer";
import { Prompt } from "react-router";
import Clear from "@mui/icons-material/Clear";
import { push } from "connected-react-router";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton
} from "@mui/material";
import "./LinkServicesAndLabTests.scss";
import PackageIcon from "@mui/icons-material/Redeem";
import startCase from "lodash/startCase";
import List from "../../components/List";
import { tl } from "../../components/translate";
import { getLabTest } from "../../api/assessment";
import PageControl from "../../components/PageControl";
import { LabTest } from "../../interfaces/LabInterfaces";
import { notificationAdd } from "../../actions/notification";
import { ServiceInterface } from "../../interfaces/ServiceInterface";
import { QueryProps } from "../../interfaces/ProductInterface";
import DebouncedTextField from "../../components/DebouncedTextField";
import OkhatiDialog from "../../components/Dialog/Dialog";
import { getServicesForMassLinking, linkServicesAndLabTests } from "../../api/services";

const LinkServicesAndLabTests = (): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isDebouncing, setIsDebouncing] = React.useState(false);
  const [labTests, setLabTests] = React.useState<LabTest[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState<string | null>(null);
  const [originalServices, setOriginalServices] = React.useState<ServiceInterface[]>([]);

  const [services, setServices] = React.useState<{
    results: ServiceInterface[];
    total: number;
  }>({
    results: [],
    total: 0
  });
  const [query, setQuery] = React.useState<Partial<QueryProps>>({
    page: 0,
    pageSize: 50,
    search: ""
  });
  const [servicesToLink, setServicesToLink] = React.useState<
    { serviceId: number; labTestId: number | null }[]
  >([]);

  const handleLinkLabTest = async () => {
    setIsUpdating(true);
    try {
      await linkServicesAndLabTests(servicesToLink);

      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          variant: "success",
          autoTimeout: true,
          message: "Lab tests linked successfully"
        })
      );

      setServicesToLink([]);
      if (pendingNavigation) {
        // dispatch(push()) and history.push
        // is not working here, so using window.location.href
        window.location.href = pendingNavigation;
        setPendingNavigation(null);
      }
    } catch (error) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          variant: "error",
          autoTimeout: true,
          message: error?.data?.message || error?.message || "Error linking/Mapping lab tests/Vat"
        })
      );
    } finally {
      setIsUpdating(false);
      setShowConfirmDialog(false);
    }
  };

  // get lab tests and services
  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const [labTestRes, serviceRes] = await Promise.all([
          getLabTest(true),
          getServicesForMassLinking({ ...query, onlyLabTest: true })
        ]);
        setLabTests(labTestRes);
        setServices(serviceRes);
        setOriginalServices(serviceRes.results);
      } catch (error) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            autoTimeout: true,
            message: error?.data?.message || error?.message || "Error fetching data"
          })
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [query]);

  return (
    <Box className="massLinkserviceList">
      <Box sx={{ height: "calc(100% - 40px)" }}>
        <Prompt
          when={servicesToLink.length > 0}
          message={(location) => {
            setPendingNavigation(location.pathname);
            setShowConfirmDialog(true);
            return false;
          }}
        />

        {showConfirmDialog && (
          <OkhatiDialog
            title="Unsaved Changes"
            description="You have unsaved changes. Do you want to save them before leaving?"
            next={handleLinkLabTest}
            cancel={() => {
              setPendingNavigation(null);
              setShowConfirmDialog(false);
            }}
            readMode={false}
          />
        )}

        <List
          data={services.results || []}
          rowHeight={50}
          hideCreateButton
          isResponsive
          activeRow={null}
          withoutSearch
          customButtonGroup={
            <>
              <Box
                sx={{
                  height: "35px",
                  padding: "0 10px",
                  display: "flex",
                  borderRadius: "5px",
                  alignItems: "center",
                  position: "relative",
                  width: "250px",
                  overflow: "hidden",
                  backgroundColor: "#ededed"
                }}
              >
                <SearchIcon />

                <DebouncedTextField
                  sx={{ ml: "10px" }}
                  value={query.search}
                  placeholder="Search..."
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ disableUnderline: true }}
                  onChange={(e) => {
                    setQuery({ ...query, search: e.target.value, page: 0 });
                  }}
                  debounceAt={1000}
                  setDebounceLoading={setIsDebouncing}
                />

                {isLoading || isDebouncing ? (
                  <Box sx={{ display: "grid", placeItems: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  query.search && (
                    <IconButton onClick={() => setQuery({ ...query, search: "" })} size="small">
                      <Clear />
                    </IconButton>
                  )
                )}
              </Box>
            </>
          }
          columns={[
            {
              key: "category",
              label: tl("category"),
              cellRenderer: (item) => (
                <Typography>
                  {startCase(item.category)}
                  {item.productType === "package" && (
                    <Typography>
                      {startCase(item.productType)} <PackageIcon />
                    </Typography>
                  )}
                </Typography>
              )
            },
            {
              key: "name",
              label: tl("name"),
              sortable: true,
              sortFunction: (a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1),
              cellRenderer: (item) => (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography>{item.name}</Typography>
                  <Typography variant="subtitle2" color="textDisabled">
                    {tl("rs")}
                    {item.grossTotalPrice}
                  </Typography>
                </Box>
              )
            },
            {
              key: "linkLabTest",
              label: tl("services.linkLabTest"),
              sortable: true,
              sortFunction: (a, b) => {
                if (!a.productisableId) return -1;
                if (!b.productisableId) return 1;

                return a.productisableId > b.productisableId ? 1 : -1;
              },
              cellRenderer: (row) => (
                <Autocomplete
                  value={
                    row?.productisableId
                      ? labTests.find((test) => test.id === row.productisableId)
                      : (() => {
                          const serviceToLinkItem = servicesToLink.find(
                            (item) => item.serviceId === Number(row.id)
                          );
                          return serviceToLinkItem
                            ? labTests.find((test) => test.id === serviceToLinkItem.labTestId)
                            : null;
                        })()
                  }
                  options={labTests}
                  getOptionLabel={(option) => option.name}
                  onChange={(_, newValue) => {
                    const productisableId = newValue?.id || null;

                    setServices((prevServices) =>
                      produce(prevServices, (draft) => {
                        const service = draft.results.find((s) => s.id === Number(row.id));
                        if (service) {
                          service.productisableId = productisableId;
                        }
                      })
                    );

                    setServicesToLink((prev) =>
                      produce(prev, (draft) => {
                        const index = draft.findIndex((item) => item.serviceId === Number(row.id));
                        if (index !== -1) {
                          draft[index].labTestId = productisableId;
                        } else {
                          draft.push({
                            serviceId: Number(row.id),
                            labTestId: productisableId
                          });
                        }
                      })
                    );
                  }}
                  sx={{ width: "250px" }}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} margin="dense" variant="outlined" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                />
              )
            }
          ]}
        />
      </Box>

      <Box
        sx={{
          height: "40px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          justifyContent: "flex-end"
        }}
      >
        <PageControl
          page={query.page || 0}
          pageSize={query.pageSize}
          maximumDataCount={services.total || 0}
          onSetPage={(value) => setQuery({ ...query, page: value })}
        />

        <Button
          variant="outlined"
          onClick={() => {
            if (servicesToLink.length > 0) {
              setServicesToLink([]);
              setServices((prev) => ({
                ...prev,
                results: originalServices
              }));
            } else {
              dispatch(push("/services"));
            }
          }}
          disabled={isUpdating}
          color="error"
          sx={{ marginLeft: "10px" }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          onClick={handleLinkLabTest}
          disabled={isUpdating || servicesToLink.length === 0}
          sx={{ marginLeft: "10px" }}
        >
          Save Services {servicesToLink.length > 0 && `(${servicesToLink.length})`}
        </Button>
      </Box>
    </Box>
  );
};

export default LinkServicesAndLabTests;
